import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { REACTIONS, reactionEmoji } from "@lunary/app-core/reactions";
import styles from "./reactions.module.css";

export { REACTIONS, reactionEmoji };

const ADD_ICON = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="12" r="8" />
    <path d="M8 13.5c.9 1.3 2.1 2 3.3 2s2.2-.7 3-2" />
    <circle cx="8.8" cy="10" r=".6" />
    <circle cx="13.4" cy="10" r=".6" />
    <path d="M19 4v4M17 6h4" />
  </svg>
);

export function ReactionStrip({
  value,
  onPick,
  placement = "up",
  center = false,
}: {
  value: number | null;
  onPick: (next: number | null) => void;
  placement?: "up" | "down";
  center?: boolean;
}) {
  return (
    <div
      className={`${styles.strip} ${placement === "down" ? styles.down : ""} ${center ? styles.center : ""}`}
      role="menu"
      aria-label="Реакции"
    >
      {REACTIONS.map((item) => (
        <button
          key={item.value}
          type="button"
          role="menuitemradio"
          aria-checked={value === item.value}
          aria-label={item.label}
          className={styles.pick}
          onClick={(event) => {
            event.stopPropagation();
            onPick(value === item.value ? null : item.value);
          }}
        >
          {item.emoji}
        </button>
      ))}
    </div>
  );
}

export function useDismiss<T extends HTMLElement>(
  open: boolean,
  close: () => void,
) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);
  return ref;
}

type ReactionButtonProps = {
  value: number | null;
  onChange: (next: number | null) => void;
  label?: string;
  center?: boolean;
};

export function ReactionButton({
  value,
  onChange,
  label = "Реакция на карту",
  center = false,
}: ReactionButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));
  const emoji = reactionEmoji(value);
  return (
    <div className={styles.root} ref={ref}>
      <button
        type="button"
        className={styles.trigger}
        data-has={emoji ? "true" : undefined}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {emoji ? <span className={styles.emoji}>{emoji}</span> : ADD_ICON}
      </button>
      {open && (
        <ReactionStrip
          value={value}
          center={center}
          onPick={(next) => {
            setOpen(false);
            onChange(next);
          }}
        />
      )}
    </div>
  );
}

type MessageReactionsProps = {
  mine: number | null;
  other: number | null;
  onChange?: (next: number | null) => void;
  children?: ReactNode;
  align: "start" | "end";
  className?: string;
  inline?: boolean;
};

const LONG_PRESS_MS = 450;

export function MessageReactions({
  mine,
  other,
  onChange,
  children,
  align,
  className,
  inline = false,
}: MessageReactionsProps) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clear, []);
  const mineEmoji = reactionEmoji(mine);
  const otherEmoji = reactionEmoji(other);
  const same = mineEmoji !== null && mineEmoji === otherEmoji;
  const chips: { key: string; emoji: string; mine: boolean; count: number }[] =
    [];
  if (same && mineEmoji)
    chips.push({ key: "both", emoji: mineEmoji, mine: true, count: 2 });
  else {
    if (otherEmoji)
      chips.push({ key: "other", emoji: otherEmoji, mine: false, count: 1 });
    if (mineEmoji)
      chips.push({ key: "mine", emoji: mineEmoji, mine: true, count: 1 });
  }
  return (
    <div
      ref={ref}
      className={[
        styles.msg,
        align === "end" ? styles.msgEnd : "",
        inline ? styles.inline : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      onPointerDown={(event) => {
        if (!onChange || event.pointerType === "mouse") return;
        clear();
        timer.current = setTimeout(() => setOpen(true), LONG_PRESS_MS);
      }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      onContextMenu={(event) => {
        if (
          onChange &&
          (event.target as HTMLElement).closest("[data-reactable]")
        ) {
          event.preventDefault();
          setOpen(true);
        }
      }}
    >
      {children}
      {chips.length > 0 && (
        <div className={styles.chips}>
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className={styles.chip}
              data-mine={chip.mine ? "true" : undefined}
              disabled={!onChange || !chip.mine}
              aria-label={chip.mine ? "Снять реакцию" : "Реакция собеседника"}
              onClick={() => onChange?.(null)}
            >
              <span>{chip.emoji}</span>
              {chip.count > 1 && <small>{chip.count}</small>}
            </button>
          ))}
        </div>
      )}
      {onChange && (
        <button
          type="button"
          className={styles.add}
          aria-label="Добавить реакцию"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {ADD_ICON}
        </button>
      )}
      {open && onChange && (
        <div
          className={`${styles.pop} ${align === "end" ? styles.popEnd : ""}`}
        >
          <ReactionStrip
            value={mine}
            placement="up"
            onPick={(next) => {
              setOpen(false);
              onChange(next);
            }}
          />
        </div>
      )}
    </div>
  );
}
